import {
    SonoraMeetingTimeSecondaryText,
    SonoraMeetingTimeTooltipText,
    SonoraMeetingTimeInlineText,
} from '../SonoraItemResults.locstring.json';
import {
    getRequiredAttendeesFromRecipients,
    getOptionalAttendeesFromRecipients,
} from './getAttendeesFromRecipients';
import { logSimpleSonoraDatapoint } from '../../dataLogger/logSonoraDatapoint';
import { SONORA_MEETING_DEEP_LINK_CREATED } from '../../constants';
import type { Recipients, SonoraMeetingTimes } from 'owa-editor-sonora-types';
import { formatWeekDayMonthDayYear, getISOString } from 'owa-datetime';
import loc from 'owa-localize';
import formatMeetingTimeSpan from 'owa-timeformat/lib/utils/formatMeetingTimeSpan';
import { getTimeZoneDisplayName } from 'owa-timezone';
import { formatSingleDayTimeSpanWithoutDate } from 'owa-timeformat/lib/utils/formatTimeSpan';

const CALENDAR_COMPOSE_DEEPLINK: string = '/calendar/deeplink/compose?';
const MAX_ATTENDEES_FOR_DEEPLINK = 5;

export const getPrimaryTextForSonoraResultsMeetingTimeItems = (
    sonoraItem: SonoraMeetingTimes
): string => {
    return formatWeekDayMonthDayYear(sonoraItem.date);
};

export const getSecondaryTextForSonoraResultsMeetingTimeItems = (
    sonoraItem: SonoraMeetingTimes
): string => {
    return loc(SonoraMeetingTimeSecondaryText, sonoraItem.timeSlots.length);
};

export const getTooltipStringForSonoraResultsMeetingTimeItems = (
    sonoraItem: SonoraMeetingTimes
): string => {
    return (
        loc(SonoraMeetingTimeTooltipText) +
        '\n' +
        sonoraItem.timeSlots
            .map(timeSlot => formatSingleDayTimeSpanWithoutDate(timeSlot.start, timeSlot.end))
            .join('\n')
    );
};

export const insertMeetingTimeSuggestionsIntoComposeEditor = (
    sonoraItem: SonoraMeetingTimes,
    fromAddress: string,
    recipients: Recipients | undefined,
    subject: string,
    insertNodeCallback: (nodeToInsert: HTMLElement) => void
): void => {
    const required = getRequiredAttendeesFromRecipients(
        recipients,
        MAX_ATTENDEES_FOR_DEEPLINK,
        false /* shouldKeepInternalAttendeesOnly */
    );
    const optional = getOptionalAttendeesFromRecipients(recipients, MAX_ATTENDEES_FOR_DEEPLINK);

    // Use white-space: pre to preserve white space and newlines.
    const paragraphNode = document.createElement('p');
    paragraphNode.setAttribute('style', 'white-space: pre;');
    paragraphNode.textContent = loc(SonoraMeetingTimeInlineText);

    const ulNode = document.createElement('ul');
    sonoraItem.timeSlots.forEach(timeSlot => {
        const liNode = document.createElement('li');
        const linkNode = document.createElement('a');
        linkNode.setAttribute(
            'href',
            getDeeplinkUrlForMeetingTimeSuggestion(
                subject,
                getISOString(timeSlot.start),
                getISOString(timeSlot.end),
                fromAddress,
                required,
                optional
            )
        );
        linkNode.setAttribute('target', '_blank');
        linkNode.textContent = `${formatMeetingTimeSpan(
            timeSlot.start,
            timeSlot.end
        )} ${getTimeZoneDisplayName(sonoraItem.date.tz)}`;
        liNode.appendChild(linkNode);
        ulNode.appendChild(liNode);
    });

    paragraphNode.appendChild(ulNode);

    insertNodeCallback(paragraphNode);

    // TODO: 119326 - pass in telemetry info
    logSimpleSonoraDatapoint(SONORA_MEETING_DEEP_LINK_CREATED, {
        required: required.length,
        optional: optional.length,
    });
};

const getDeeplinkUrlForMeetingTimeSuggestion = (
    subject: string,
    startdt: string,
    enddt: string,
    from: string,
    to: string[],
    cc: string[]
): string => {
    const params = [
        `subject=${subject}`,
        `startdt=${startdt}`,
        `enddt=${enddt}`,
        `to=${[from, ...to].join(';')}`,
        `cc=${cc.join(';')}`,
    ].join('&');

    return window.location.origin + CALENDAR_COMPOSE_DEEPLINK + params;
};
